import { delay } from 'redux-saga'
import normalize from 'jsonapi-normalizer'
import T from 'i18n-react'
import {
  takeLatest,
  take,
  fork,
  all,
  call,
  cancel,
  select,
  put,
} from 'redux-saga/effects'
import * as R from 'ramda'

import {
  getRequest,
  post,
  del,
} from 'helpers/RequestUtil'

import {
  countSelector,
  pageSizeSelector,
  currentPageSelector,
  searchValueSelector,
} from './selectors'

import {
  selectedValueSelector,
  selectedSelector,
} from 'containers/Admin/Client/ClientSelection/selectors'

import {
  UPDATE_ACTIVE_DIALOG,
  DIALOG_TYPE
} from 'containers/Admin/Client/Client/constants'

import {
  CHANGE_PARTNER_SITE_ENABLE,
  CHANGE_CURRENT_PAGE,
  CHANGE_PAGE_SIZE,
  CHANGE_SEARCH_VALUE,
  FETCH_PARTNER_SITES,
  POST_PARTNER_SITE,
  DELETE_PARTNER_SITE,
} from './constants'

import {
  updatePageSize,
  updateCountSize,
  updateCurrentPage,
  updateSearchValue,
  clearPartnerSiteList,
  updatePartnerSiteList,
  fetchPartnerSitesStart,
  fetchPartnerSitesSuccess,
  fetchPartnerSitesError,
  startFetchPartnerSitesAsync,
  postPartnerSite as enablePartnerSite,
  postPartnerSiteStart,
  postPartnerSiteSuccess,
  postPartnerSiteError,
  startPostPartnerSiteAsync,
  deletePartnerSite as disablePartnerSite,
  deletePartnerSiteStart,
  deletePartnerSiteSuccess,
  deletePartnerSiteError,
  startDeletePartnerSiteAsync,
} from './actions'

import {
  success,
  error,
} from 'containers/App/Notification/actions'

import {
  updateTablesAction,
} from 'containers/Entity/actions'

function* postPartnerSite({id, mappingId, clientId, ...value}, client) {
  try {
    yield put(startPostPartnerSiteAsync())
    yield put(postPartnerSiteStart())
    const data = yield call(
      post,
      'partner_sites',
      {
        clientId,
        partnerSiteId: id
      },
      '/api/fulcrum/client/partner_sites'
    )
    const mappingId = R.path(['result', 'partner_sites', '0'])(data)
    const normalized = {
      result: { client_partner_sites: [id] },
      entities: { client_partner_sites: { [id]: { ...value, id, mappingId, clientId } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(postPartnerSiteSuccess())
    yield put(success(T.translate(
      'pages.admin.clients.notification.post_associate_success',
      {
        name: client.name,
        site: value.name,
        status: 'Enabled'
      }
    )))
    return data
  } catch(error) {
    yield put(postPartnerSiteError(error))
    yield put(error(T.translate(
      'pages.admin.clients.notification.post_associate_error',
      {
        name: client.name,
        site: value.name,
      }
    )))
  }
}

function* deletePartnerSite({id, mappingId, ...value}, client) {
  try {
    yield put(startDeletePartnerSiteAsync())
    yield put(deletePartnerSiteStart())
    const data = yield call(
      del,
      `/api/fulcrum/client/partner_sites/${mappingId}`
    )
    const normalized = {
      result: { client_partner_sites: [id] },
      entities: { client_partner_sites: { [id]: { ...value, id, mappingId: null} } }
    }
    yield put(updateTablesAction(normalized))
    yield put(deletePartnerSiteSuccess())
    yield put(success(T.translate(
      'pages.admin.clients.notification.post_associate_success',
      {
        name: client.name,
        site: value.name,
        status: 'Disabled'
      }
    )))
    return data
  } catch(error) {
    yield put(deletePartnerSiteError(error))
    yield put(error(T.translate(
      'pages.admin.clients.notification.post_associate_error',
      {
        name: client.name,
        site: value.name,
      }
    )))
  }
}

function* fetchPartnerSites(url) {
  try {
    yield put(startFetchPartnerSitesAsync())
    yield put(fetchPartnerSitesStart())
    const data = yield call(
      getRequest,
      url
    )
    const normalized = normalize(data)
    yield put(updateTablesAction(normalized))
    yield put(fetchPartnerSitesSuccess())
    return data
  } catch(error) {
    yield put(fetchPartnerSitesError(error))
  }
}

function* handlePostPartnerSite(action) {
  const { value } = action
  const client = yield select(selectedSelector)
  yield call(postPartnerSite, value, client)
}

function* handleDeletePartnerSite(action) {
  const { value } = action
  const client = yield select(selectedSelector)
  yield call(deletePartnerSite, value, client)
}

function* handleChangePartnerSiteEnable(action) {
  const { value } = action
  if (R.isNil(value.mappingId))
    yield put(enablePartnerSite(value))
  else
    yield put(disablePartnerSite(value))
}

const buildUrl = (id, search, size, page) =>
  `/api/fulcrum/clients/${id}/partner_sites?page[number]=${page}&page[size]=${size}&filter[search]=${search}`

function* handleFetchPartnerSites() {
  yield call(delay, 500)
  const page = yield select(currentPageSelector)
  const size = yield select(pageSizeSelector)
  const search = yield select(searchValueSelector)
  const id = yield select(selectedValueSelector)
  const url = buildUrl(id, search, size, page)
  const data = yield call(fetchPartnerSites, url)
  const normalized = normalize(data)
  const { result: { client_partner_sites } } = normalized
  yield put(updateCountSize(data.meta.totalCount))
  yield put(updatePartnerSiteList(client_partner_sites))
}

function* handleChangePageSize(action) {
  const { size } = action
  const page = yield select(currentPageSelector)
  const prevSize = yield select(pageSizeSelector)
  let newPage = Math.floor((page - 1) * prevSize / size)
  newPage = (newPage > 0)
    ? newPage
    : newPage + 1

  yield put(updatePageSize(size))
  yield put(updateCurrentPage(newPage))
  yield fork(handleFetchPartnerSites)
}

function* handleChangeCurrentPage(action) {
  const { value } = action
  const page = yield select(currentPageSelector)
  yield put(updateCurrentPage(page + value))
  yield fork(handleFetchPartnerSites)
}

function* handleSearchChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(clearPartnerSiteList())
  yield put(updateSearchValue(value))
  yield fork(handleFetchPartnerSites)
}

function* watchPostPartnerSite() {
  yield takeLatest(POST_PARTNER_SITE, handlePostPartnerSite)
}

function* watchDeletePartnerSite() {
  yield takeLatest(DELETE_PARTNER_SITE, handleDeletePartnerSite)
}

function* watchChangePartnerSiteEnable() {
  yield takeLatest(CHANGE_PARTNER_SITE_ENABLE, handleChangePartnerSiteEnable)
}

function* watchFetchPartnerSites() {
  yield takeLatest(FETCH_PARTNER_SITES, handleFetchPartnerSites)
}

function* watchChangePageSize() {
  yield takeLatest(CHANGE_PAGE_SIZE, handleChangePageSize)
}

function* watchChangeCurrentPage() {
  yield takeLatest(CHANGE_CURRENT_PAGE, handleChangeCurrentPage)
}

function* watchSearchChange() {
  yield takeLatest(CHANGE_SEARCH_VALUE, handleSearchChange)
}

function* watchUpdateActiveDialog() {
  let task
  while(true) {
    const { active, value } = yield take(UPDATE_ACTIVE_DIALOG)
    if (task)
      yield cancel(task)

    if (active && value === DIALOG_TYPE.UPDATE) {
      yield put(updateCurrentPage(1))
      yield put(clearPartnerSiteList())
      task = yield fork(handleFetchPartnerSites)
    }
  }
}

function* loadSagas() {
  yield all([
    fork(watchFetchPartnerSites),
    fork(watchChangePageSize),
    fork(watchChangeCurrentPage),
    fork(watchSearchChange),
    fork(watchPostPartnerSite),
    fork(watchDeletePartnerSite),
    fork(watchChangePartnerSiteEnable),
    fork(watchUpdateActiveDialog),
  ])
}

export default loadSagas
